import { useMemo, useState } from 'react';
import api from '../api/axios';
import { suggestCity } from './locationFix';

const INPUT_CLS =
  'w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 ' +
  'transition-shadow focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';

// values   = array of raw location strings (one per record, duplicates allowed)
// endpoint = e.g. '/agent/employers/bulk-city'
// noun     = 'employer' | 'job'
export default function FixLocationsModal({ values, endpoint, noun, onClose, onApplied }) {
  const [edits, setEdits] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState('');

  const groups = useMemo(() => {
    const map = new Map();
    values.forEach((v) => { if (v) map.set(v, (map.get(v) || 0) + 1); });
    return Array.from(map, ([raw, count]) => ({ raw, count, suggested: suggestCity(raw) }))
      .filter((g) => g.suggested !== g.raw)
      .sort((a, b) => b.count - a.count);
  }, [values]);

  const apply = async (g) => {
    const to = (edits[g.raw] ?? g.suggested).trim();
    if (!to) return;
    setBusyKey(g.raw);
    setError('');
    try {
      await api.put(endpoint, { from: [g.raw], to });
      onApplied(g.raw, to);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update location.');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-purple-700 to-pink-600 px-6 py-5 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">Fix Locations</h3>
            <p className="text-purple-100 text-xs mt-0.5">
              {groups.length} spelling{groups.length === 1 ? '' : 's'} need review. Edit the correct value, then click Apply.
            </p>
          </div>
          <button onClick={onClose} className="text-purple-100 hover:text-white text-xl leading-none">×</button>
        </div>

        <div className="p-5 overflow-y-auto">
          {error && <p className="mb-3 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
          {groups.length === 0 && (
            <p className="text-center text-gray-400 py-10">All locations look correct 🎉</p>
          )}
          <div className="space-y-2">
            {groups.map((g) => (
              <div key={g.raw} className="flex items-center gap-3 border border-gray-100 rounded-xl px-4 py-2.5">
                <div className="w-1/3 min-w-0">
                  <p className="text-sm font-medium text-red-600 truncate" title={g.raw}>{g.raw}</p>
                  <p className="text-[11px] text-gray-400">{g.count} {noun}{g.count > 1 ? 's' : ''}</p>
                </div>
                <span className="text-gray-300">→</span>
                <input
                  value={edits[g.raw] ?? g.suggested}
                  onChange={(e) => setEdits((p) => ({ ...p, [g.raw]: e.target.value }))}
                  className={INPUT_CLS + ' flex-1'}
                />
                <button
                  onClick={() => apply(g)}
                  disabled={busyKey === g.raw}
                  className="bg-gradient-to-r from-purple-600 to-pink-500 text-white text-xs font-semibold px-4 py-2 rounded-full disabled:opacity-50"
                >
                  {busyKey === g.raw ? '…' : 'Apply'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}